import { Injectable } from '@angular/core';
import { Observable, Subject, catchError, shareReplay, throwError } from 'rxjs';

import {
  ClientDto,
  ProjectDto,
  ProjectsApiService,
} from './projects-api.service';

type ProjectListParams = {
  client_id?: string;
  ids?: string[];
};

@Injectable({ providedIn: 'root' })
export class ProjectsStoreService {
  private readonly projectCache = new Map<string, Observable<ProjectDto>>();
  private readonly projectValues = new Map<string, ProjectDto>();
  private readonly listCache = new Map<string, Observable<ProjectDto[]>>();
  private readonly listValues = new Map<string, ProjectDto[]>();
  private readonly clientCache = new Map<string, Observable<ClientDto[]>>();
  private readonly clientValues = new Map<string, ClientDto[]>();
  private readonly projectUpdatesSubject = new Subject<string>();

  readonly projectUpdates$ = this.projectUpdatesSubject.asObservable();

  constructor(private readonly api: ProjectsApiService) {}

  upsertProject(project: ProjectDto) {
    this.projectCache.delete(project._id);
    this.projectValues.set(project._id, project);
    this.clearCollectionCaches();
    this.projectUpdatesSubject.next(project._id);
  }

  removeProject(projectId: string) {
    this.projectCache.delete(projectId);
    this.projectValues.delete(projectId);
    this.clearCollectionCaches();
    this.projectUpdatesSubject.next(projectId);
  }

  invalidateProject(projectId: string) {
    this.projectCache.delete(projectId);
    this.projectValues.delete(projectId);
    this.clearCollectionCaches();
    this.projectUpdatesSubject.next(projectId);
  }

  refreshProject(id: string): Observable<ProjectDto> {
    this.projectCache.delete(id);
    this.projectValues.delete(id);
    return this.getProject(id);
  }

  getCachedProject(id: string): ProjectDto | null {
    return this.projectValues.get(id) ?? null;
  }

  getProject(id: string): Observable<ProjectDto> {
    if (this.projectCache.has(id)) {
      return this.projectCache.get(id)!;
    }

    const request$ = this.api.getById(id).pipe(
      shareReplay(1),
      catchError((err) => {
        this.projectCache.delete(id);
        return throwError(() => err);
      }),
    );

    request$.subscribe({
      next: (project) => {
        this.projectValues.set(id, project);
      },
      error: () => undefined,
    });

    this.projectCache.set(id, request$);
    return request$;
  }

  listProjects(params?: ProjectListParams): Observable<ProjectDto[]> {
    const key = JSON.stringify({
      client_id: params?.client_id ?? null,
      ids: params?.ids ?? null,
    });

    if (this.listCache.has(key)) {
      return this.listCache.get(key)!;
    }

    const request$ = this.api.listProjects(params).pipe(
      shareReplay(1),
      catchError((err) => {
        this.listCache.delete(key);
        return throwError(() => err);
      }),
    );

    request$.subscribe({
      next: (projects) => {
        const list = projects ?? [];
        this.listValues.set(key, list);
        list.forEach((project) => {
          this.projectValues.set(project._id, project);
        });
      },
      error: () => undefined,
    });

    this.listCache.set(key, request$);
    return request$;
  }

  listClients(populated = false): Observable<ClientDto[]> {
    const key = populated ? 'populated' : 'simple';
    if (this.clientCache.has(key)) {
      return this.clientCache.get(key)!;
    }

    const request$ = this.api.listClients(populated).pipe(
      shareReplay(1),
      catchError((err) => {
        this.clientCache.delete(key);
        return throwError(() => err);
      }),
    );

    request$.subscribe({
      next: (clients) => {
        const list = clients ?? [];
        this.clientValues.set(key, list);
        list.forEach((client) => {
          (client.projects ?? []).forEach((project) => {
            this.projectValues.set(project._id, project);
          });
        });
      },
      error: () => undefined,
    });

    this.clientCache.set(key, request$);
    return request$;
  }

  private clearCollectionCaches() {
    this.listCache.clear();
    this.listValues.clear();
    this.clientCache.clear();
    this.clientValues.clear();
  }
}
